import { useEffect, useState } from 'react';

/**
 * Detects if the current device is mobile based on viewport width and user agent.
 * Works with Chrome DevTools device emulation and iframe embedding.
 *
 * @returns true if the device is mobile (≤768px viewport or mobile user agent)
 */
export const getIsMobile = (): boolean => {
  if (typeof window === 'undefined') return false;

  // Use documentElement.clientWidth which gives actual viewport width
  const viewportWidth = document.documentElement.clientWidth;

  // If viewport is still default (980), check user agent as fallback
  if (viewportWidth >= 980) {
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileDevice =
      /iphone|ipod|android|blackberry|windows phone|opera mini|iemobile/i.test(
        userAgent
      );
    return isMobileDevice;
  }

  return viewportWidth <= 768;
};

/**
 * Subscribes to window resize; keeps `getIsMobile()` in sync for FAB and layout.
 */
export function useIsMobileWidget(): boolean {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? getIsMobile() : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return isMobile;
}
